import { Injectable } from '@angular/core';
import { defer, forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { AuthService } from './auth-service.service';
import { DocumentsStorageService } from './documents-storage.service';
import {
  DocumentsForUploadRowData,
  DocumentsForUploadRowType
} from '../db-description';
import { ForwardingUploadService } from './forwarding-upload.service';
import { calculateDocumentChecksum } from '../utils/document-checksum.util';
import { XlsxTemplateFillerServiceService } from './docs-service/xlsx-template-filler-service.service';
import {
  FORWARDER_REPORT_TPL_PATH,
  TRN_TEMPLATE_TPL_PATH,
  TTN_TEMPLATE_TPL_PATH
} from '../utils/constants/document-template.constants';
import { ExcelGeneratorData } from '../dto/excel-generation-data.dto';
import { AppModeStatus, OfflineModeService } from './offline-mode.service';

@Injectable({
  providedIn: 'root'
})
export class DocumentsSubmitService {
  private submitting: boolean = false;

  constructor(
    private readonly authService: AuthService,
    private readonly docGeneratorService: XlsxTemplateFillerServiceService,
    private readonly offlineModeService: OfflineModeService,
    private readonly storageService: DocumentsStorageService,
    private readonly uploadService: ForwardingUploadService
  ) {}

  public submitPendingDocuments(): Observable<boolean> {
    if (!this.canSubmit()) {
      return of(false);
    }
    this.submitting = true;
    return this.storageService.getRecentDocs().pipe(
      switchMap(docs =>
        forkJoin(
          docs.map(doc =>
            // defer: чтобы синхронный throw в submitDocumentIfNecessary (неподдерживаемый тип,
            // отсутствие orderNumber/vehicleDataId, сбой сериализации контрольной суммы) стал ошибкой
            // ВНУТРИ потока и был изолирован catchError. Без defer бросок вылетел бы при построении
            // массива для forkJoin и оборвал бы отправку ВСЕЙ очереди.
            defer(() => this.submitDocumentIfNecessary(doc)).pipe(
              catchError(err => of(err))
            )
          )
        )
      ),
      map(results => {
        const error: Error = results.find(
          item => item instanceof Error
        ) as Error;
        if (error) {
          throw error;
        }
        return true;
      }),
      finalize(() => {
        this.submitting = false;
      })
    );
  }

  private canSubmit(): boolean {
    return (
      !this.submitting &&
      this.authService.isLoggedIn() &&
      this.offlineModeService.appModeStatus$.getValue() === AppModeStatus.ONLINE
    );
  }

  private submitDocumentIfNecessary(
    docRow: DocumentsForUploadRowData
  ): Observable<unknown> {
    const checksum: string = calculateDocumentChecksum(docRow.data);
    if (docRow.uploadedChecksum === checksum) {
      return of(false);
    }
    return this.submitDocument(docRow).pipe(
      catchError(err => {
        const detail = err?.message ?? String(err);
        throw new Error(`Ошибка загрузки документа на сервер: ${detail}`);
      }),
      switchMap(() =>
        this.storageService.storeDoc({
          data: docRow.data,
          docType: docRow.docType,
          forwardingRequestId: docRow.forwardingRequestId,
          orderNumber: docRow.orderNumber,
          uploadedChecksum: checksum,
          vehicleDataId: docRow.vehicleDataId
        })
      )
    );
  }

  private submitDocument(row: DocumentsForUploadRowData): Observable<boolean> {
    switch (row.docType) {
      case DocumentsForUploadRowType.FORWARDER_REPORT:
        return this.submitForwarderReport(row);
      case DocumentsForUploadRowType.TTN:
        return this.submitTtnDocument(row);
      case DocumentsForUploadRowType.TRN:
        return this.submitTrnDocument(row);
      default:
        break;
    }
    throw new Error(`Неподдерживаемый тип документа: ${row.docType}`);
  }

  private submitForwarderReport(
    row: DocumentsForUploadRowData
  ): Observable<boolean> {
    return from(
      this.docGeneratorService.generateDocument(
        FORWARDER_REPORT_TPL_PATH,
        row.data as ExcelGeneratorData
      )
    ).pipe(
      switchMap((generatedDoc: Blob) =>
        this.uploadService.uploadForwarderReport({
          document: generatedDoc,
          forwardingRequestId: row.forwardingRequestId
        })
      ),
      map(() => true)
    );
  }

  private submitTtnDocument(
    row: DocumentsForUploadRowData
  ): Observable<boolean> {
    if (row.orderNumber == null) {
      throw new Error('Отсутствует обязательный аттрибут: orderNumber');
    }
    if (row.vehicleDataId == null) {
      throw new Error('Отсутствует обязательный аттрибут: vehicleDataId');
    }
    return from(
      this.docGeneratorService.generateDocument(
        TTN_TEMPLATE_TPL_PATH,
        row.data as ExcelGeneratorData
      )
    ).pipe(
      switchMap((generatedDoc: Blob) =>
        this.uploadService.uploadTtnDocument({
          document: generatedDoc,
          forwardingRequestId: row.forwardingRequestId,
          orderNumber: row.orderNumber,
          vehicleDataId: row.vehicleDataId
        })
      ),
      map(() => true)
    );
  }

  private submitTrnDocument(
    row: DocumentsForUploadRowData
  ): Observable<boolean> {
    if (row.orderNumber == null) {
      throw new Error('Отсутствует обязательный аттрибут: orderNumber');
    }
    if (row.vehicleDataId == null) {
      throw new Error('Отсутствует обязательный аттрибут: vehicleDataId');
    }
    return from(
      this.docGeneratorService.generateDocument(
        TRN_TEMPLATE_TPL_PATH,
        row.data as ExcelGeneratorData
      )
    ).pipe(
      switchMap((generatedDoc: Blob) =>
        this.uploadService.uploadTrnDocument({
          document: generatedDoc,
          forwardingRequestId: row.forwardingRequestId,
          orderNumber: row.orderNumber,
          vehicleDataId: row.vehicleDataId
        })
      ),
      map(() => true)
    );
  }
}
